/*
 * @Description: file content
 * @Author:
 * @Date: 2023-08-22 11:43:42
 * @LastEditors: Lxc 1533761120@qq.com
 * @LastEditTime: 2024-08-30 17:42:41
 */
import { useRoute } from 'vue-router'
import Clipboard from 'clipboard'
import { useWorkspaceStoreWithout } from '@/store/modules/workspace'

export function getURLParameters(url = decodeURIComponent(window.location.href)) {
  // 提取URL中最后一个问号后的参数，并将其解析为一个对象。
  const params: any = {}
  const queryString: any = url.split('?').pop()

  queryString.split('&').forEach((param: any) => {
    const keyValue = param.split('=')
    params[keyValue[0]] = decodeURIComponent(keyValue[1])
  })

  return params
}

interface HighLightLabel {
  highWord: string
  label: string
  hightColor?: string
  separate?: string
}
/**
 * 关键字高亮(须在文字显示标签内加v-html)
 * @param {*} highWord 需要高亮的关键字
 * @param {*} label 文字内容
 * @param {*} hightColor 高亮颜色
 * @param {*} separate 分隔符，默认','
 * @returns 已高亮关键字的内容
 */
export const highLightLabel = ({ highWord, label, hightColor = '#ff4080', separate = ',' }: HighLightLabel) => {
  if (highWord && label) {
    const arr = highWord.split(separate)
    // 词的长度较长的放在前面先高亮，如果按顺序遍历的话，比如高亮['精华', '精华液']，会出现只高亮'精华'
    const sortArr = arr.sort((a, b) => b.length - a.length)
    // const escapeRegExp = (string) => string.replace(/[-[\]{}()*+?.,\\^$|]/g, '\\$&');

    // 匹配html标签
    const regHtml = /<.*?>/ig

    sortArr.forEach((item) => {
      if (item.trim()) {
        let num = -1
        const escapedItem = item// escapeRegExp(item);
        const replaceReg = new RegExp(escapedItem, 'ig')
        // 将识别到的html标签存起来
        const arrHtml = label.match(regHtml)
        label = label.replace(regHtml, '{~}').replace(replaceReg, (match) => {
          return `<span style="color: ${hightColor}">${match}</span>`
        }).replace(/{~}/g, () => {
          // 将html标签替换回来
          num++
          return arrHtml ? arrHtml[num] : ''
        })
      }
    })
  }
  return label
}

/**
 * 页面点击后posthog传的action路径
 * @param {string[]} pathNameArr 路径要拼接的名称
 * @returns {string}
 */
export function getPosthogPath(pathNameArr?: string[]) {
  const route = useRoute()

  const matchedRoute = route.matched

  const pagePage = matchedRoute.reduce((acc, cur) => {
    if (cur.meta.posthogTitle) {
      return acc.length > 0 ? `${acc}:${cur.meta.posthogTitle}` : cur.meta.posthogTitle
    } else {
      return acc
    }
  }, '')

  if (!pathNameArr || pathNameArr.length === 0) {
    return pagePage
  }
  // console.log('埋点-----', `${pagePage}:${pathNameArr.join(':')}`)
  return `${pagePage}:${pathNameArr.join(':')}`
}

// 工具方法，为 URL 添加 workspaceId 参数
export function openWindowWithWorkspace(url, enterpriseId?: number) {
  const workspaceStore = useWorkspaceStoreWithout()
  const workspaceId = workspaceStore.workspaceId || enterpriseId

  // 检查 URL 是否以 http 或 https 开头
  if (!/^https?:\/\//i.test(url)) {
    // 如果不是，构建完整的绝对 URL
    const baseUrl = `${window.location.protocol}//${window.location.host}`
    url = new URL(url, baseUrl).toString()
  }

  // 创建一个 URL 对象，它会自动处理现有的查询参数
  const urlObject = new URL(url)

  // 添加或更新 workspaceId 查询参数
  if (workspaceId) {
    urlObject.searchParams.set('workspace', String(workspaceId))
  }

  // 使用 window.open 打开新的标签页，传递处理后的 URL
  window.open(urlObject.toString(), '_blank')
}

export function simulateClickOutside() {
  // 创建一个新的'click'事件
  const clickOutsideEvent = new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    view: window,
  })

  // 分发事件
  document.dispatchEvent(clickOutsideEvent)
}
/**
 * 获取文件后缀名
 * @param {string} filename 文件名称
 * @returns {string} 后缀名
 */
export const getFileSuffix = (filename: string): string => {
  if (!filename) {
    return ''
  }

  try {
    // 尝试解析为URL，可能会抛出异常
    const url = new URL(filename)
    // 使用pathname而不是完整的URL来获取没有查询参数的路径
    filename = url.pathname
  } catch (error) {
    // 如果URL无效，将捕获异常，但是可以安全地忽略它
    // 因为这意味着filename不是一个有效的URL，而是一个普通的文件名
  }

  // 按点分割文件名获取后缀名
  const arr = filename.split('.')
  return arr.length > 1 ? arr[arr.length - 1].toLocaleLowerCase() : ''
}

/**
 * 获取文件类型
 * @param {string} filename 文件名称
 * @returns {string} 文件类型
 */
export const getFileType = (filename: string) => {
  const suffix = getFileSuffix(filename)

  let type = ''
  switch (suffix) {
    case 'pdf':
      type = 'pdf'
      break
    case 'ppt':
    case 'pptx':
      type = 'ppt'
      break
    case 'txt':
      type = 'txt'
      break
    case 'json':
      type = 'json'
      break
    case 'doc':
    case 'docx':
      type = 'word'
      break
    case 'xls':
    case 'xlsx':
      type = 'excel'
      break
    case 'gif':
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'webp':
    case 'tif':
    case 'wmf':
    case 'dib':
      type = 'image'
      break
    case 'mp4':
      type = 'video'
      break
    default:
      type = 'unknown'
      break
  }
  return type
}

export function loadJs(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = src
    document.body.appendChild(script)

    script.onload = () => {
      resolve(null)
    }
    script.onerror = () => {
      reject(new Error(`Failed to load script: ${src}`))
    }
  })
}

export const chunk = (arr, size) => {
  return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
    arr.slice(i * size, i * size + size),
  )
}

/**
 * 复制文本到剪贴板
 * @param text 文本内容
 */
export const copyText = async (text: string): Promise<boolean> => {
  if (!text) {
    console.warn('复制的文本内容为空')
    return false
  }

  // 优先使用现代 Clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text)
      // console.log('复制成功 (Clipboard API):', text)
      return true
    } catch (err) {
      console.warn('Clipboard API 失败，尝试降级方案:', err)
    }
  }

  // 降级方案1: 使用 execCommand (虽然已弃用但兼容性好)
  try {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    const successful = document.execCommand('copy')
    document.body.removeChild(textArea)

    if (successful) {
      console.log('复制成功 (execCommand):', text)
      return true
    }
  } catch (err) {
    console.warn('execCommand 复制失败:', err)
  }

  // 降级方案2: 使用 clipboard.js 作为最后的选择
  return new Promise((resolve) => {
    try {
      const fakeElement = document.createElement('button')
      const clipboard = new Clipboard(fakeElement, {
        text: () => text,
        action: () => 'copy',
        container: document.body,
      })

      clipboard.on('success', () => {
        clipboard.destroy()
        console.log('复制成功 (clipboard.js):', text)
        resolve(true)
      })

      clipboard.on('error', (e) => {
        clipboard.destroy()
        console.error('clipboard.js 复制失败:', e)
        resolve(false)
      })

      document.body.appendChild(fakeElement)
      fakeElement.click()
      document.body.removeChild(fakeElement)
    } catch (err) {
      console.error('clipboard.js 初始化失败:', err)
      resolve(false)
    }
  })
}
